import { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { motion } from 'framer-motion';
import { Trophy, Award, ArrowUpRight, Sparkles, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Brand tokens (match Promotions page for cohesion)
const SKY = '#35B7E8';
const SKY_DEEP = '#1F8FBE';
const NAVY = '#0B1437';
const NAVY_SOFT = '#2B3674';
const MUTED = '#7B8597';
const FAINT = '#A3AED0';

const fmtUSD = (n) => `$${Math.round(Number(n || 0)).toLocaleString()}`;
const fmtUSDShort = (n) => {
  const x = Number(n || 0);
  if (x >= 1000) return `$${(x / 1000).toFixed(x >= 10000 ? 0 : 1)}k`;
  return `$${Math.round(x)}`;
};

const CARD = 'rounded-[20px] bg-white shadow-[0px_18px_40px_rgba(112,144,176,0.12)]';

// ---- Circular % or text badge (mirrors Promotions style, compact) ----
const MiniBadge = ({ pct, text, active = false, size = 56 }) => (
  <div
    className="relative flex shrink-0 items-center justify-center rounded-full"
    style={{
      width: size, height: size,
      background: active
        ? `linear-gradient(135deg, ${SKY} 0%, #4FC3F7 50%, ${SKY_DEEP} 100%)`
        : '#F4F7FE',
      border: active ? '1.5px solid rgba(255,255,255,0.4)' : `1.5px solid ${'#E5EBF5'}`,
      boxShadow: active ? `0 8px 24px ${SKY}40, inset 0 1px 0 rgba(255,255,255,0.5)` : 'none',
    }}
  >
    <span
      className="font-bold tabular-nums"
      style={{
        color: active ? 'white' : MUTED,
        fontSize: text ? size * 0.26 : size * 0.32,
        fontFamily: '"Manrope", system-ui, sans-serif',
        letterSpacing: '-0.02em',
      }}
    >
      {text != null ? text : `${pct}%`}
    </span>
  </div>
);

const Progress = ({ value, color = SKY }) => (
  <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#EEF2F9]">
    <motion.div
      className="h-full rounded-full"
      initial={{ width: 0 }}
      animate={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      style={{ background: `linear-gradient(90deg, ${SKY_DEEP}, ${color})`, boxShadow: value > 0 ? `0 0 8px ${color}40` : 'none' }}
    />
  </div>
);

// ============================================================
// PROMOTION WIDGET
// ============================================================
export function PromotionWidget() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/promotions/me`, getAuthHeader())
      .then(r => setData(r.data))
      .catch(e => {
        console.error('[PromotionWidget] fetch failed:', e?.response?.status, e?.response?.data || e?.message);
        setError(e?.response?.status === 404 ? 'API not available on this server' : 'Failed to load');
      })
      .finally(() => setLoading(false));
  }, [getAuthHeader]);

  if (loading) {
    return (
      <div className={`${CARD} flex h-full items-center justify-center p-6`} data-testid="dashboard-promotion-widget">
        <Loader2 className="h-5 w-5 animate-spin" style={{ color: SKY }} />
      </div>
    );
  }
  if (error || !data || !data.agent) {
    return (
      <div className={`${CARD} p-6`} data-testid="dashboard-promotion-widget">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: `${SKY}15` }}>
            <Trophy className="h-4 w-4" style={{ color: SKY_DEEP }} />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: SKY_DEEP }}>Promotion</div>
            <h3 className="font-['Manrope'] text-lg font-bold" style={{ color: NAVY }}>Your contract</h3>
          </div>
        </div>
        <div className="mt-4 rounded-xl p-3 text-xs" style={{ background: '#F4F7FE', color: MUTED }}>
          {error || 'Promotions data unavailable.'}
        </div>
      </div>
    );
  }

  const { agent, current_month, next_rung, target_eval } = data;
  const currentTitle = agent.current_title || `${agent.current_level_pct}% contract`;

  // Best of the two paths (whichever is further along) for the progress hint
  const pAP = current_month?.personal_ap || 0;
  const tAP = current_month?.team_ap_qualifying || 0;
  const pThr = next_rung?.personal_threshold;
  const tThr = next_rung?.team_threshold;
  const pPct = pThr ? Math.min(100, (pAP / pThr) * 100) : 0;
  const tPct = tThr ? Math.min(100, (tAP / tThr) * 100) : 0;

  const useTeam = (tPct > pPct) || pThr == null;
  const pathLabel = useTeam ? 'Team AP' : 'Personal AP';
  const pathAP = useTeam ? tAP : pAP;
  const pathThr = useTeam ? tThr : pThr;
  const pathPct = useTeam ? tPct : pPct;
  const isQualified = target_eval?.qualified;

  return (
    <motion.button
      type="button"
      onClick={() => navigate('/promotions')}
      whileHover={{ y: -2 }}
      className={`${CARD} group relative w-full overflow-hidden p-6 text-left transition-shadow hover:shadow-[0px_24px_50px_rgba(112,144,176,0.18)]`}
      data-testid="dashboard-promotion-widget"
    >
      {/* Decorative gradient orb */}
      <div className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full opacity-40 blur-3xl"
        style={{ background: `radial-gradient(circle, ${SKY}55, transparent 70%)` }} />

      <div className="relative">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: `${SKY}15` }}>
              <Trophy className="h-4 w-4" style={{ color: SKY_DEEP }} />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: SKY_DEEP }}>Promotion</div>
              <h3 className="font-['Manrope'] text-lg font-bold" style={{ color: NAVY }}>Your contract</h3>
            </div>
          </div>
          <ArrowUpRight className="h-4 w-4 opacity-40 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" style={{ color: NAVY_SOFT }} />
        </div>

        {/* Current level */}
        <div className="mt-5 flex items-center gap-4">
          <MiniBadge pct={agent.current_level_pct} active size={64} />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: FAINT }}>You are here</div>
            <div className="mt-0.5 font-['Manrope'] text-lg font-extrabold tracking-tight" style={{ color: NAVY }}>
              {currentTitle}
            </div>
          </div>
        </div>

        {/* Up next */}
        {next_rung ? (
          <div className="mt-5 rounded-xl p-3" style={{ background: '#F7FAFE', border: '1px solid #E5EBF5' }}>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[9px] font-bold uppercase tracking-[0.2em]" style={{ color: FAINT }}>Up next</div>
                <div className="truncate text-xs font-semibold" style={{ color: NAVY }}>
                  {next_rung.title} <span style={{ color: MUTED }}>· {next_rung.level_pct}%</span>
                </div>
              </div>
              {isQualified ? (
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-600">Qualified</span>
              ) : null}
            </div>
            <div className="mt-2.5">
              <div className="mb-1 flex items-center justify-between text-[10px]" style={{ color: MUTED }}>
                <span className="font-semibold" style={{ color: NAVY_SOFT }}>{pathLabel}</span>
                <span className="tabular-nums">{fmtUSDShort(pathAP)} / {fmtUSDShort(pathThr)}</span>
              </div>
              <Progress value={pathPct} />
            </div>
          </div>
        ) : (
          <div className="mt-5 rounded-xl p-3 text-center" style={{ background: `linear-gradient(135deg, ${SKY}15, ${SKY}05)`, border: `1px solid ${SKY}30` }}>
            <Sparkles className="mx-auto mb-1 h-4 w-4" style={{ color: SKY_DEEP }} />
            <div className="text-xs font-semibold" style={{ color: NAVY }}>Top of the ladder</div>
          </div>
        )}
      </div>
    </motion.button>
  );
}

// ============================================================
// BONUS TRACKER WIDGET
// ============================================================
export function BonusWidget() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/bonuses/me`, getAuthHeader())
      .then(r => setData(r.data))
      .catch(e => {
        console.error('[BonusWidget] fetch failed:', e?.response?.status, e?.response?.data || e?.message);
        setError(e?.response?.status === 404 ? 'API not available on this server' : 'Failed to load');
      })
      .finally(() => setLoading(false));
  }, [getAuthHeader]);

  if (loading) {
    return (
      <div className={`${CARD} flex h-full items-center justify-center p-6`} data-testid="dashboard-bonus-widget">
        <Loader2 className="h-5 w-5 animate-spin" style={{ color: SKY }} />
      </div>
    );
  }
  if (error || !data || data.net_issued_life_ap == null) {
    return (
      <div className={`${CARD} p-6`} data-testid="dashboard-bonus-widget">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: `${SKY}15` }}>
            <Award className="h-4 w-4" style={{ color: SKY_DEEP }} />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: SKY_DEEP }}>Monthly Bonus</div>
            <h3 className="font-['Manrope'] text-lg font-bold" style={{ color: NAVY }}>This month</h3>
          </div>
        </div>
        <div className="mt-4 rounded-xl p-3 text-xs" style={{ background: '#F4F7FE', color: MUTED }}>
          {error || 'Bonus data unavailable.'}
        </div>
      </div>
    );
  }

  const {
    current_month, net_issued_life_ap, current_bonus, current_tier,
    next_tier, gap_to_next, marginal_cash, is_maxed, below_floor,
  } = data;

  const ap = net_issued_life_ap || 0;
  const daysLeft = current_month?.days_remaining ?? 0;

  // Progress fill: between current tier (or 0) and next tier (or top tier ap)
  const floor = current_tier ? current_tier.tier_ap : 0;
  const ceiling = next_tier ? next_tier.tier_ap : ap;
  const pctToNext = ceiling > floor ? Math.min(100, ((ap - floor) / (ceiling - floor)) * 100) : 100;

  return (
    <motion.button
      type="button"
      onClick={() => navigate('/promotions')}
      whileHover={{ y: -2 }}
      className={`${CARD} group relative w-full overflow-hidden p-6 text-left transition-shadow hover:shadow-[0px_24px_50px_rgba(112,144,176,0.18)]`}
      data-testid="dashboard-bonus-widget"
    >
      <div className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full opacity-40 blur-3xl"
        style={{ background: `radial-gradient(circle, ${SKY}55, transparent 70%)` }} />

      <div className="relative">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: `${SKY}15` }}>
              <Award className="h-4 w-4" style={{ color: SKY_DEEP }} />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: SKY_DEEP }}>Monthly Bonus</div>
              <h3 className="font-['Manrope'] text-lg font-bold" style={{ color: NAVY }}>{current_month?.label || 'This month'}</h3>
            </div>
          </div>
          <ArrowUpRight className="h-4 w-4 opacity-40 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" style={{ color: NAVY_SOFT }} />
        </div>

        {/* Locked bonus */}
        <div className="mt-5 flex items-center gap-4">
          <MiniBadge
            text={current_tier ? fmtUSDShort(current_tier.tier_ap) : '$0'}
            active={current_bonus > 0}
            size={64}
          />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: FAINT }}>Locked in</div>
            <div className="mt-0.5 font-['Manrope'] text-lg font-extrabold tracking-tight tabular-nums" style={{ color: current_bonus > 0 ? NAVY : MUTED }}>
              {fmtUSD(current_bonus)}
            </div>
            <div className="text-[11px] tabular-nums" style={{ color: MUTED }}>
              {fmtUSD(ap)} net life AP
            </div>
          </div>
        </div>

        {/* Up next / status */}
        {!is_maxed && next_tier ? (
          <div className="mt-5 rounded-xl p-3" style={{ background: '#F7FAFE', border: '1px solid #E5EBF5' }}>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[9px] font-bold uppercase tracking-[0.2em]" style={{ color: FAINT }}>Up next</div>
                <div className="truncate text-xs font-semibold" style={{ color: NAVY }}>
                  {fmtUSD(next_tier.cash_bonus)} bonus <span style={{ color: MUTED }}>· +{fmtUSD(marginal_cash)}</span>
                </div>
              </div>
              <span className="rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider tabular-nums" style={{ background: `${SKY}15`, color: SKY_DEEP }}>
                {daysLeft}d left
              </span>
            </div>
            <div className="mt-2.5">
              <div className="mb-1 flex items-center justify-between text-[10px]" style={{ color: MUTED }}>
                <span className="font-semibold" style={{ color: NAVY_SOFT }}>
                  {below_floor ? 'To first tier' : 'To next tier'}
                </span>
                <span className="tabular-nums">{fmtUSDShort(gap_to_next)} to go</span>
              </div>
              <Progress value={pctToNext} />
            </div>
          </div>
        ) : (
          <div className="mt-5 rounded-xl p-3 text-center" style={{ background: `linear-gradient(135deg, ${SKY}15, ${SKY}05)`, border: `1px solid ${SKY}30` }}>
            <Sparkles className="mx-auto mb-1 h-4 w-4" style={{ color: SKY_DEEP }} />
            <div className="text-xs font-semibold" style={{ color: NAVY }}>Top tier reached · $25,000</div>
          </div>
        )}
      </div>
    </motion.button>
  );
}

// ============================================================
// CAROUSEL — swipeable on mobile, side-by-side grid on md+
// ============================================================
export function PromoBonusCarousel() {
  const scrollerRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);

  const onScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    // Each card is one "page" — figure out which is closest to center
    const center = el.scrollLeft + el.clientWidth / 2;
    const children = Array.from(el.children);
    let nearest = 0;
    let nearestDist = Infinity;
    children.forEach((c, i) => {
      const childCenter = c.offsetLeft + c.offsetWidth / 2;
      const d = Math.abs(childCenter - center);
      if (d < nearestDist) {
        nearestDist = d;
        nearest = i;
      }
    });
    if (nearest !== activeIdx) setActiveIdx(nearest);
  }, [activeIdx]);

  const scrollToIdx = (i) => {
    const el = scrollerRef.current;
    if (!el) return;
    const child = el.children[i];
    if (child) {
      el.scrollTo({ left: child.offsetLeft - (el.clientWidth - child.offsetWidth) / 2, behavior: 'smooth' });
    }
  };

  return (
    <div data-testid="promo-bonus-carousel">
      {/* Scroller — mobile: horizontal snap carousel; md+: normal grid */}
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        className="flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-2 -mx-4 scroll-smooth scrollbar-hide md:mx-0 md:grid md:grid-cols-2 md:gap-6 md:overflow-visible md:px-0 md:pb-0 md:snap-none"
        style={{ scrollbarWidth: 'none' }}
      >
        <div className="w-[88%] shrink-0 snap-center md:w-auto md:shrink">
          <PromotionWidget />
        </div>
        <div className="w-[88%] shrink-0 snap-center md:w-auto md:shrink">
          <BonusWidget />
        </div>
      </div>

      {/* Dot indicator — mobile only */}
      <div className="mt-3 flex items-center justify-center gap-1.5 md:hidden">
        {[0, 1].map((i) => (
          <button
            key={i}
            type="button"
            onClick={() => scrollToIdx(i)}
            aria-label={`Go to widget ${i + 1}`}
            className="h-1.5 rounded-full transition-all"
            style={{
              width: i === activeIdx ? 18 : 6,
              background: i === activeIdx ? '#35B7E8' : '#D9E1F2',
            }}
            data-testid={`promo-bonus-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
}

